import { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  doc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../contexts/UserContext";
import { trackPeriodLogged } from "./useAnalytics";
import { CycleSettings, PeriodEntry, PredictedPeriod } from "../types";
import {
  getCyclePhase,
  getOvulationWindow,
  getNextPrediction,
  CyclePhaseInfo,
  OvulationWindow,
} from "../utils/CycleCalculations";

export interface UseCycleDataResult {
  // State
  cycleSettings: CycleSettings | null;
  periodEntries: PeriodEntry[];
  currentPeriod: PeriodEntry | null;
  cyclePhase: CyclePhaseInfo | null;
  ovulationWindow: OvulationWindow | null;
  nextPrediction: PredictedPeriod | null;
  cycleEnabled: boolean;
  loading: boolean;
  // CRUD
  createPeriod: (entry: Omit<PeriodEntry, "id" | "userId" | "createdAt">) => Promise<void>;
  endPeriod: (endDate?: string) => Promise<void>;
  updatePeriod: (id: string, updates: Partial<PeriodEntry>) => Promise<void>;
  deletePeriod: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
  /** Create day log entries for specific dates (used by calendar edit save). */
  ensureDayLogsForDates: (dates: string[]) => Promise<void>;
}

const useCycleData = (): UseCycleDataResult => {
  const { userDetails } = useUser()!;
  const uid = userDetails?.user?.uid;

  const [cycleSettings, setCycleSettings] = useState<CycleSettings | null>(null);
  const [periodEntries, setPeriodEntries] = useState<PeriodEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!uid) {
      setLoading(false);
      return;
    }

    try {
      // Fetch cycle settings
      const settingsRef = collection(db, "cycleSettings");
      const settingsQ = query(settingsRef, where("userId", "==", uid));
      const settingsSnap = await getDocs(settingsQ);

      if (settingsSnap.empty || !settingsSnap.docs[0].data().enabled) {
        setCycleSettings(
          settingsSnap.empty
            ? null
            : ({ ...settingsSnap.docs[0].data(), id: settingsSnap.docs[0].id } as CycleSettings)
        );
        setPeriodEntries([]);
        setLoading(false);
        return;
      }

      const settings = {
        ...settingsSnap.docs[0].data(),
        id: settingsSnap.docs[0].id,
      } as CycleSettings;
      setCycleSettings(settings);

      // Fetch period entries (collection name is "periodEntry")
      const entriesRef = collection(db, "periodEntry");
      const entriesQ = query(entriesRef, where("userId", "==", uid), orderBy("startDate", "desc"), limit(120));
      const entriesSnap = await getDocs(entriesQ);
      const entries = entriesSnap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      })) as PeriodEntry[];

      setPeriodEntries(entries);
    } catch (error) {
      console.error("[CycleData] Error fetching:", error);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const currentPeriod = useMemo(
    () => periodEntries.find((e) => !e.endDate) || null,
    [periodEntries]
  );

  const cycleEnabled = !!cycleSettings?.enabled;

  const cyclePhase = useMemo(
    () =>
      cycleEnabled && periodEntries.length > 0 && cycleSettings
        ? getCyclePhase(periodEntries, cycleSettings)
        : null,
    [cycleEnabled, periodEntries, cycleSettings]
  );

  const ovulationWindow = useMemo(
    () =>
      cycleEnabled && periodEntries.length > 0 && cycleSettings
        ? getOvulationWindow(periodEntries, cycleSettings)
        : null,
    [cycleEnabled, periodEntries, cycleSettings]
  );

  const nextPrediction = useMemo(
    () =>
      cycleEnabled && periodEntries.length > 0 && cycleSettings
        ? getNextPrediction(periodEntries, cycleSettings)
        : null,
    [cycleEnabled, periodEntries, cycleSettings]
  );

  // ── Helpers: link period entries to cycle day logs ──

  /** Ensure a cycleDayLog doc exists for a given date with flow data. */
  const ensureDayLog = useCallback(
    async (date: string, flow: "spotting" | "light" | "medium" | "heavy") => {
      if (!uid) return;
      const logsRef = collection(db, "cycleDayLogs");
      const snap = await getDocs(query(logsRef, where("userId", "==", uid), where("date", "==", date)));
      const now = new Date().toISOString();
      if (snap.empty) {
        await addDoc(logsRef, {
          userId: uid,
          date,
          symptoms: [],
          activities: [],
          mood: null,
          energy: null,
          flow,
          testResult: null,
          notes: "",
          createdAt: now,
          updatedAt: now,
        });
      } else if (!snap.docs[0].data().flow) {
        // Existing log but no flow set — fill it in
        await updateDoc(snap.docs[0].ref, { flow, updatedAt: now });
      }
    },
    [uid]
  );

  /** Create day logs for every day of a period with sensible flow defaults. */
  const ensureDayLogsForRange = useCallback(
    async (startDate: string, endDate: string) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const totalDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
      const cursor = new Date(start);
      const promises: Promise<void>[] = [];

      for (let i = 0; i < totalDays && i < 14; i++) {
        const dateStr = cursor.toISOString().split("T")[0];
        // Sensible flow defaults: heavy early, light at end
        const flow: "heavy" | "medium" | "light" =
          i < Math.ceil(totalDays * 0.3) ? "heavy" : i < Math.ceil(totalDays * 0.7) ? "medium" : "light";
        promises.push(ensureDayLog(dateStr, flow));
        cursor.setDate(cursor.getDate() + 1);
      }
      await Promise.all(promises);
    },
    [ensureDayLog]
  );

  // CRUD

  const createPeriod = useCallback(
    async (entry: Omit<PeriodEntry, "id" | "userId" | "createdAt">) => {
      if (!uid) return;
      const newEntry = { ...entry, userId: uid, createdAt: new Date().toISOString() };
      const docRef = await addDoc(collection(db, "periodEntry"), newEntry);
      const entryWithId = { ...newEntry, id: docRef.id } as PeriodEntry;
      setPeriodEntries((prev) => [entryWithId, ...prev]);
      trackPeriodLogged("start");
      // Only auto-create a day log for live "Start Period" (no endDate yet).
      // Calendar bulk saves pass endDate — skip those to avoid mass-creating
      // day logs for historical periods that get recreated during edit.
      if (!entry.endDate) {
        ensureDayLog(entry.startDate, "medium").catch(console.error);
      }
    },
    [uid, ensureDayLog]
  );

  const endPeriod = useCallback(
    async (endDateStr?: string) => {
      if (!currentPeriod) return;
      const endDate = endDateStr || new Date().toISOString().split("T")[0];
      const docRef = doc(db, "periodEntry", currentPeriod.id);
      await updateDoc(docRef, { endDate });
      setPeriodEntries((prev) =>
        prev.map((e) => e.id === currentPeriod.id ? { ...e, endDate } : e)
      );
      trackPeriodLogged("end");
      // Auto-create day logs for the full period range
      ensureDayLogsForRange(currentPeriod.startDate, endDate).catch(console.error);
    },
    [currentPeriod, ensureDayLogsForRange]
  );

  const updatePeriod = useCallback(
    async (periodId: string, updates: Partial<PeriodEntry>) => {
      const docRef = doc(db, "periodEntry", periodId);
      const { id: _, ...safeUpdates } = updates as any;
      await updateDoc(docRef, safeUpdates);
      setPeriodEntries((prev) =>
        prev.map((e) => (e.id === periodId ? { ...e, ...updates } : e))
      );
    },
    []
  );

  const deletePeriod = useCallback(
    async (periodId: string) => {
      const docRef = doc(db, "periodEntry", periodId);
      await deleteDoc(docRef);
      setPeriodEntries((prev) => prev.filter((e) => e.id !== periodId));
    },
    []
  );

  /** Create day log entries for a list of specific dates (calendar edit). */
  const ensureDayLogsForDates = useCallback(
    async (dates: string[]) => {
      await Promise.all(dates.map((d) => ensureDayLog(d, "medium")));
    },
    [ensureDayLog]
  );

  return {
    cycleSettings,
    periodEntries,
    currentPeriod,
    cyclePhase,
    ovulationWindow,
    nextPrediction,
    cycleEnabled,
    loading,
    createPeriod,
    endPeriod,
    updatePeriod,
    deletePeriod,
    refetch: fetchData,
    ensureDayLogsForDates,
  };
};

export default useCycleData;
